import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type ThemeMode = 'light' | 'dark';

const STORAGE_KEY = 'aa-theme';

/**
 * Light / dark mode. The colours themselves are CSS variables in styles.scss, switched by
 * <html data-theme="dark">. Light is always the default (the device's own setting is ignored);
 * dark is only used once someone picks it, and the choice is remembered per browser
 * (index.html applies it before the app loads).
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly modeSubject = new BehaviorSubject<ThemeMode>(this.readSaved());
  readonly mode$: Observable<ThemeMode> = this.modeSubject.asObservable();

  constructor() {
    this.apply(this.modeSubject.value);
  }

  get mode(): ThemeMode {
    return this.modeSubject.value;
  }

  setMode(mode: ThemeMode): void {
    try {
      // Light is the default, so only dark needs remembering
      if (mode === 'dark') localStorage.setItem(STORAGE_KEY, 'dark');
      else localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Private browsing or blocked storage: the choice still applies until the page is closed
    }
    this.apply(mode);
    this.modeSubject.next(mode);
  }

  toggle(): ThemeMode {
    const next: ThemeMode = this.mode === 'dark' ? 'light' : 'dark';
    this.setMode(next);
    return next;
  }

  private apply(mode: ThemeMode): void {
    const root = document.documentElement;
    if (mode === 'dark') root.setAttribute('data-theme', 'dark');
    else root.removeAttribute('data-theme');
  }

  private readSaved(): ThemeMode {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'dark' ? 'dark' : 'light';
    } catch {
      return 'light';
    }
  }
}
